'use client';

import { useTranslation } from 'next-i18next';
import { Button, LinkStyled } from '@components/common';
import React, { useEffect, useRef } from 'react';
import { CheckIcon } from '@heroicons/react/16/solid';
import { useCompanyJobActions } from '@components/templates/my-jobs/hooks/useCompanyJobActions';
import toast from 'react-hot-toast/headless';
import { useExtendedRouter, useExtendedSearchParams } from '@hooks/next-routing-wrappers';

/**
 * @description Offer Page SearchPack
 */
interface SearchPackInfoCardProps {
  isAuth:boolean,
}


export const OfferPageSearchPackage = ({
  isAuth,
}: SearchPackInfoCardProps) => {
  const { t } = useTranslation('common');
  const { searchParamsAsObject } = useExtendedSearchParams();
  const searchPackageRef = useRef<HTMLDivElement>(null);
  const { router } = useExtendedRouter();

  const {
    handleSearchpackConfirm,
    isActivatingSearchpack,
  } = useCompanyJobActions({
    onSuccesSearchpackActivation: () => {
      toast.success(t('my-jobs.toast.activate.searchpack'));
    },
    jobSlug: '',
    currentJobState: 'active',
  });

  useEffect(() => {
    if (searchParamsAsObject?.scrollTo === 'search-package' && searchPackageRef.current) {
      setTimeout(() => {
        searchPackageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const newParams = new URLSearchParams(window.location.search);
        newParams.delete('scrollTo');
        const newRelativePathQuery = window.location.pathname + (newParams.toString() ? `?${newParams.toString()}` : '');

        // Use router.replace to update URL without refreshing or adding history entry
        router.replace(newRelativePathQuery, { scroll: false });
      }, 100);
    }
  }, [searchParamsAsObject, router]);

  return (
    <>
      <div ref={searchPackageRef} className="flex h-full flex-col justify-between  border-transparent text-center">
        <div>
          <h3 className="mb-5 text-left font-bold">
            {t('global.searchpack.title')}
          </h3>


          <div className="mt-5 flex items-center gap-5">
            <div className="">
              <div className="mb-1 flex items-start gap-2">
                <CheckIcon className="size-5 min-w-5 text-secondary" />
                <span className="text-left">{t('global.searchpack.benefit.2')}</span>
              </div>
              <div className="mb-1 flex items-start gap-2">
                <CheckIcon className="size-5 min-w-5 text-secondary" />
                <span className="text-left">{t('global.searchpack.benefit.1')}</span>
              </div>
              <div className="mb-1 flex items-start gap-2">
                <CheckIcon className="size-5 min-w-5 text-secondary" />
                <span className="text-left">{t('global.searchpack.benefit.3')}</span>
              </div>
            </div>
          </div>
        </div>


        <div className="mt-10 flex flex-col items-start justify-start">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold md:text-4xl">
              200
            </span>
            <span className="text-ink-medium">
              {t('services.page.currency')}
              {' '}
              + TVA
            </span>
          </div>

          {!isAuth ? (
            <LinkStyled
              href={`/login?targetPath=${encodeURIComponent('/company/offer?scrollTo=search-package')}`}
              styling="solid"
              size="sm"
              color="secondary"
              rounding="full"
              className="mt-3"
              aria-label={t('global.action.searchpack.activate')}
            >
              {t('global.action.searchpack.activate')}
            </LinkStyled>

          ) : (
            <Button
              styling="solid"
              size="sm"
              disabled={false}
              color="secondary"
              rounding="full"
              className="mt-3"
              isLoading={isActivatingSearchpack}
              aria-label={t('my-jobs.card.actions.searchpack')}
              onClick={handleSearchpackConfirm}
              spinnerClass="size-6"
            >
              {t('global.action.searchpack.activate')}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
